import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import api from "../lib/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "react-datepicker/dist/react-datepicker.css";
import "./CSS/CustomerCreditPremium.css";
import "./CSS/ExpensesPremium.css";
import {
  FaChartLine,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaFileExport,
  FaFilter,
  FaLayerGroup,
  FaMoneyBillWave,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaTimes,
  FaTrashAlt,
  FaWallet,
} from "react-icons/fa";

const pageSize = 5;
const refRequiredModes = new Set(["upi", "card", "bank_transfer", "neft", "rtgs"]);
const paymentModes = ["cash", "upi", "card", "bank_transfer", "credit", "other"];
const recurringFrequencies = ["daily", "weekly", "monthly", "quarterly", "yearly"];

function parseSafeDate(value) {
  if (!value) return null;
  const normalized = value instanceof Date ? value : String(value).replace(" ", "T");
  const d = value instanceof Date ? value : new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toDateOnly(value) {
  const d = parseSafeDate(value);
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toDateTimeInput(value) {
  const d = parseSafeDate(value);
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

function formatDateTime(value) {
  const d = parseSafeDate(value);
  if (!d) return "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function money(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function startOfDay(value) {
  const d = parseSafeDate(value) || new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(value) {
  const d = parseSafeDate(value) || new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

function daysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getPageNumbers(activePage, totalPages) {
  if (totalPages <= 1) return [1];
  const pages = new Set([1, totalPages, activePage, activePage - 1, activePage + 1]);
  return Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
}

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "warning" || notice.type === "error"
      ? FaExclamationCircle
      : FaWallet;

  return (
    <div
      className={`cc-toast ${notice.type}`}
      role={notice.type === "warning" || notice.type === "error" ? "alert" : "status"}
      aria-live={notice.type === "warning" || notice.type === "error" ? "assertive" : "polite"}
    >
      <div className="cc-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="cc-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function StatCard({ tone, icon: Icon, label, value, caption }) {
  return (
    <article className={`cc-summary-card ${tone}`}>
      <div className="cc-summary-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-summary-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <em>{caption}</em>
      </div>
    </article>
  );
}

function MiniBar({ label, value, total, tone = "blue" }) {
  const width = total > 0 ? Math.max(6, Math.round((value / total) * 100)) : 0;
  return (
    <div className="exp-bar-row">
      <div className="exp-bar-label">
        <strong>{label}</strong>
        <span>{money(value)}</span>
      </div>
      <div className="exp-bar-track" aria-hidden="true">
        <span className={`exp-bar-fill ${tone}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function ConfirmModal({ open, title, message, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <div className="exp-modal-overlay" role="presentation">
      <div className="exp-modal" role="dialog" aria-modal="true" aria-labelledby="exp-modal-title">
        <div className="exp-modal-head">
          <div className="exp-modal-icon">
            <FaTrashAlt aria-hidden="true" />
          </div>
          <div>
            <span>Confirm action</span>
            <h3 id="exp-modal-title">{title}</h3>
          </div>
        </div>
        <p>{message}</p>
        <div className="exp-modal-actions">
          <button type="button" className="btn btn-light" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Expenses() {
  const token = localStorage.getItem("token");

  const defaultRange = useMemo(() => ({ startDate: daysAgo(29), endDate: endOfDay(new Date()) }), []);

  const [tab, setTab] = useState("expenses");
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const [savingRecurring, setSavingRecurring] = useState(false);
  const [expensePage, setExpensePage] = useState(1);
  const [recurringPage, setRecurringPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [recurringRules, setRecurringRules] = useState([]);

  const [filtersDraft, setFiltersDraft] = useState({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    category_id: "",
    payment_mode: "",
    shift: "",
  });
  const [filters, setFilters] = useState({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    category_id: "",
    payment_mode: "",
    shift: "",
  });

  const [search, setSearch] = useState("");
  const [recurringSearch, setRecurringSearch] = useState("");

  const [expenseForm, setExpenseForm] = useState({
    id: null,
    expense_date: toDateOnly(new Date()),
    expense_datetime: "",
    category_id: "",
    vendor_id: "",
    amount: "",
    payment_mode: "cash",
    payment_reference: "",
    description: "",
    shift: "",
    employee_id: "",
  });

  const [recurForm, setRecurForm] = useState({
    id: null,
    name: "",
    category_id: "",
    vendor_id: "",
    amount: "",
    frequency: "monthly",
    day_of_month: "",
    day_of_week: "",
    start_date: toDateOnly(new Date()),
    end_date: "",
    next_run_date: toDateOnly(new Date()),
    payment_mode: "cash",
    auto_post: true,
    notes: "",
    is_active: true,
  });

  const showNotice = (type, title, message) => {
    setNotice({ type, title, message });
    window.clearTimeout(showNotice._timer);
    showNotice._timer = window.setTimeout(() => setNotice(null), 3600);
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [catRes, venRes, empRes, expRes, recurRes] = await Promise.allSettled([
        api.get("/api/expenses/categories/all", { headers: { Authorization: `Bearer ${token}` } }),
        api.get("/api/vendors", { headers: { Authorization: `Bearer ${token}` } }),
        api.get("/api/users", {
          params: { scope: "tenant" },
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get("/api/expenses", {
          params: {
            ...filters,
            startDate: toDateOnly(filters.startDate),
            endDate: toDateOnly(filters.endDate),
          },
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get("/api/expenses/recurring", { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (catRes.status === "fulfilled") setCategories(catRes.value.data || []);
      else showNotice("warning", "Categories unavailable", "Could not load expense categories right now.");

      if (venRes.status === "fulfilled") setVendors(venRes.value.data || []);
      else showNotice("warning", "Vendor list unavailable", "Expense vendor picker will stay empty until vendors load.");

      if (empRes.status === "fulfilled") {
        setEmployees((empRes.value.data || []).filter((emp) => emp.role === "operator" || emp.role === "manager"));
      }
      else showNotice("warning", "Employee list unavailable", "Employee selection will stay limited until users load.");

      if (expRes.status === "fulfilled") setExpenses(expRes.value.data || []);
      else showNotice("error", "Expense load failed", "Unable to load the expense register.");

      if (recurRes.status === "fulfilled") setRecurringRules(recurRes.value.data || []);
      else showNotice("error", "Recurring load failed", "Unable to load recurring expense rules.");
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const res = await api.get("/api/expenses", {
        params: {
          ...nextFilters,
          startDate: toDateOnly(nextFilters.startDate),
          endDate: toDateOnly(nextFilters.endDate),
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setExpenses(res.data || []);
      setExpensePage(1);
    } catch (err) {
      showNotice("error", "Expense load failed", err?.response?.data?.error || "Unable to load expenses.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecurringRules = async () => {
    try {
      const res = await api.get("/api/expenses/recurring", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecurringRules(res.data || []);
      setRecurringPage(1);
    } catch (err) {
      showNotice("error", "Recurring load failed", err?.response?.data?.error || "Unable to load recurring rules.");
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const filteredExpenses = useMemo(() => {
    const q = search.trim().toLowerCase();
    return expenses.filter((row) => {
      const matchesSearch =
        !q ||
        [row.category_name, row.vendor_name, row.description, row.payment_reference, row.employee_name, row.shift, row.payment_mode]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));
      return matchesSearch;
    });
  }, [expenses, search]);

  const expenseSummary = useMemo(() => {
    const totalAmount = filteredExpenses.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const cashAmount = filteredExpenses
      .filter((row) => String(row.payment_mode || "").toLowerCase() === "cash")
      .reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const byCategory = filteredExpenses.reduce((acc, row) => {
      const key = row.category_name || "Uncategorized";
      acc[key] = (acc[key] || 0) + Number(row.amount || 0);
      return acc;
    }, {});
    const byMode = filteredExpenses.reduce((acc, row) => {
      const key = String(row.payment_mode || "other").toLowerCase();
      acc[key] = (acc[key] || 0) + Number(row.amount || 0);
      return acc;
    }, {});
    const byMonth = filteredExpenses.reduce((acc, row) => {
      const month = String(row.expense_date || "").slice(0, 7) || "Unknown";
      acc[month] = (acc[month] || 0) + Number(row.amount || 0);
      return acc;
    }, {});
    const categoryList = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);
    const monthList = Object.entries(byMonth).sort((a, b) => b[0].localeCompare(a[0]));
    const modeList = Object.entries(byMode).sort((a, b) => b[1] - a[1]);

    return {
      totalAmount,
      cashAmount,
      count: filteredExpenses.length,
      average: filteredExpenses.length ? totalAmount / filteredExpenses.length : 0,
      topCategory: categoryList[0]?.[0] || "-",
      topCategoryAmount: categoryList[0]?.[1] || 0,
      topMonth: monthList[0]?.[0] || "-",
      topMonthAmount: monthList[0]?.[1] || 0,
      modeList,
      categoryList,
      monthList,
    };
  }, [filteredExpenses]);

  const recurringSummary = useMemo(() => {
    const totalAmount = recurringRules.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const activeAuto = recurringRules.filter((row) => Number(row.auto_post || 0) === 1).length;
    const nextRun = recurringRules[0]?.next_run_date || "-";
    return {
      count: recurringRules.length,
      totalAmount,
      activeAuto,
      nextRun,
    };
  }, [recurringRules]);

  const filteredRecurringRules = useMemo(() => {
    const q = recurringSearch.trim().toLowerCase();
    return recurringRules.filter((row) => {
      if (!q) return true;
      return [row.name, row.category_name, row.vendor_name, row.frequency, row.notes]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [recurringRules, recurringSearch]);

  const expenseTotalPages = Math.max(1, Math.ceil(filteredExpenses.length / pageSize));
  const recurringTotalPages = Math.max(1, Math.ceil(filteredRecurringRules.length / pageSize));
  const safeExpensePage = Math.min(expensePage, expenseTotalPages);
  const safeRecurringPage = Math.min(recurringPage, recurringTotalPages);
  const expenseRows = filteredExpenses.slice((safeExpensePage - 1) * pageSize, safeExpensePage * pageSize);
  const recurringRows = filteredRecurringRules.slice((safeRecurringPage - 1) * pageSize, safeRecurringPage * pageSize);

  useEffect(() => {
    setExpensePage(1);
  }, [search]);

  useEffect(() => {
    setRecurringPage(1);
  }, [recurringSearch]);

  const quickRange = (days) => {
    const next = {
      ...filtersDraft,
      startDate: days === 1 ? startOfDay(new Date()) : daysAgo(days - 1),
      endDate: endOfDay(new Date()),
    };
    setFiltersDraft(next);
    setFilters(next);
    fetchExpenses(next);
  };

  const refreshAll = async () => {
    await Promise.all([fetchExpenses(filters), fetchRecurringRules()]);
    showNotice("success", "Refreshed", "Expense data is up to date.");
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      id: null,
      expense_date: toDateOnly(new Date()),
      expense_datetime: "",
      category_id: "",
      vendor_id: "",
      amount: "",
      payment_mode: "cash",
      payment_reference: "",
      description: "",
      shift: "",
      employee_id: "",
    });
  };

  const resetRecurringForm = () => {
    setRecurForm({
      id: null,
      name: "",
      category_id: "",
      vendor_id: "",
      amount: "",
      frequency: "monthly",
      day_of_month: "",
      day_of_week: "",
      start_date: toDateOnly(new Date()),
      end_date: "",
      next_run_date: toDateOnly(new Date()),
      payment_mode: "cash",
      auto_post: true,
      notes: "",
      is_active: true,
    });
  };

  const saveExpense = async () => {
    const mode = String(expenseForm.payment_mode || "").toLowerCase();
    const amount = Number(expenseForm.amount || 0);

    if (!expenseForm.expense_date) {
      showNotice("warning", "Date required", "Please select an expense date.");
      return;
    }
    if (!expenseForm.category_id) {
      showNotice("warning", "Category required", "Please choose an expense category.");
      return;
    }
    if (!amount || amount <= 0) {
      showNotice("warning", "Amount required", "Please enter a valid expense amount.");
      return;
    }
    if (refRequiredModes.has(mode) && !String(expenseForm.payment_reference || "").trim()) {
      showNotice("warning", "Reference required", "Please enter a payment reference for this mode.");
      return;
    }
    if (expenseForm.expense_datetime) {
      const datePart = String(expenseForm.expense_datetime).slice(0, 10);
      if (datePart && expenseForm.expense_date && datePart !== expenseForm.expense_date) {
        showNotice("warning", "Date mismatch", "The expense date and timestamp should belong to the same day.");
        return;
      }
    }

    const payload = {
      expense_date: expenseForm.expense_date,
      expense_datetime: expenseForm.expense_datetime || null,
      category_id: expenseForm.category_id,
      vendor_id: expenseForm.vendor_id || null,
      amount,
      payment_mode: expenseForm.payment_mode,
      payment_reference: expenseForm.payment_reference || null,
      description: expenseForm.description || null,
      shift: expenseForm.shift || null,
      employee_id: expenseForm.employee_id || null,
    };

    setSavingExpense(true);
    try {
      if (expenseForm.id) {
        await api.put(`/api/expenses/${expenseForm.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice("success", "Expense updated", "The expense row has been saved.");
      } else {
        await api.post("/api/expenses", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice("success", "Expense saved", "The new expense has been recorded.");
      }
      resetExpenseForm();
      await fetchExpenses(filters);
    } catch (err) {
      showNotice("error", "Save failed", err?.response?.data?.error || "Unable to save the expense.");
    } finally {
      setSavingExpense(false);
    }
  };

  const editExpense = (row) => {
    setTab("expenses");
    setExpenseForm({
      id: row.id,
      expense_date: row.expense_date || "",
      expense_datetime: toDateTimeInput(row.expense_datetime),
      category_id: row.category_id,
      vendor_id: row.vendor_id || "",
      amount: row.amount,
      payment_mode: row.payment_mode || "cash",
      payment_reference: row.payment_reference || "",
      description: row.description || "",
      shift: row.shift || "",
      employee_id: row.employee_id || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveRecurringRule = async () => {
    const amount = Number(recurForm.amount || 0);
    const startDate = recurForm.start_date ? parseSafeDate(recurForm.start_date) : null;
    const endDate = recurForm.end_date ? parseSafeDate(recurForm.end_date) : null;
    const nextRunDate = recurForm.next_run_date ? parseSafeDate(recurForm.next_run_date) : null;

    if (!recurForm.name.trim()) {
      showNotice("warning", "Rule name required", "Please add a clear recurring rule name.");
      return;
    }
    if (!recurForm.category_id) {
      showNotice("warning", "Category required", "Please choose a category for this recurring rule.");
      return;
    }
    if (!amount || amount <= 0) {
      showNotice("warning", "Amount required", "Please enter a valid recurring amount.");
      return;
    }
    if (!recurForm.frequency) {
      showNotice("warning", "Frequency required", "Please select a recurring frequency.");
      return;
    }
    if (!recurForm.start_date || !recurForm.next_run_date) {
      showNotice("warning", "Dates required", "Please enter the start date and next run date.");
      return;
    }
    if (endDate && startDate && endDate < startDate) {
      showNotice("warning", "Date range invalid", "The end date cannot be earlier than the start date.");
      return;
    }
    if (startDate && nextRunDate && nextRunDate < startDate) {
      showNotice("warning", "Next run invalid", "Next run date cannot be earlier than the start date.");
      return;
    }
    if (recurForm.frequency === "monthly") {
      const day = Number(recurForm.day_of_month || 0);
      if (!day || day < 1 || day > 31) {
        showNotice("warning", "Day of month required", "Monthly rules need a valid day between 1 and 31.");
        return;
      }
    }
    if (recurForm.frequency === "weekly") {
      const day = Number(recurForm.day_of_week || -1);
      if (day < 0 || day > 6) {
        showNotice("warning", "Day of week required", "Weekly rules need a valid day from 0 to 6.");
        return;
      }
    }

    const payload = {
      name: recurForm.name.trim(),
      category_id: recurForm.category_id,
      vendor_id: recurForm.vendor_id || null,
      amount,
      frequency: recurForm.frequency,
      day_of_month: recurForm.day_of_month || null,
      day_of_week: recurForm.day_of_week || null,
      start_date: recurForm.start_date,
      end_date: recurForm.end_date || null,
      next_run_date: recurForm.next_run_date,
      payment_mode: recurForm.payment_mode,
      auto_post: recurForm.auto_post,
      notes: recurForm.notes || "",
      is_active: recurForm.is_active,
    };

    setSavingRecurring(true);
    try {
      if (recurForm.id) {
        await api.put(`/api/expenses/recurring/${recurForm.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice("success", "Rule updated", "The recurring rule has been saved.");
      } else {
        await api.post("/api/expenses/recurring", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice("success", "Rule saved", "The recurring rule is ready.");
      }
      resetRecurringForm();
      await fetchRecurringRules();
    } catch (err) {
      showNotice("error", "Save failed", err?.response?.data?.error || "Unable to save recurring rule.");
    } finally {
      setSavingRecurring(false);
    }
  };

  const editRecurringRule = (row) => {
    setTab("recurring");
    setRecurForm({
      id: row.id,
      name: row.name,
      category_id: row.category_id,
      vendor_id: row.vendor_id || "",
      amount: row.amount,
      frequency: row.frequency,
      day_of_month: row.day_of_month || "",
      day_of_week: row.day_of_week || "",
      start_date: row.start_date || toDateOnly(new Date()),
      end_date: row.end_date || "",
      next_run_date: row.next_run_date || toDateOnly(new Date()),
      payment_mode: row.payment_mode || "cash",
      auto_post: !!row.auto_post,
      notes: row.notes || "",
      is_active: !!row.is_active,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteExpense = async () => {
    if (!deleteTarget || deleteTarget.kind !== "expense") return;
    try {
      await api.delete(`/api/expenses/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotice("success", "Expense deleted", "The expense row has been removed.");
      await fetchExpenses(filters);
    } catch (err) {
      showNotice("error", "Delete failed", err?.response?.data?.error || "Unable to delete the expense.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const deleteRecurringRule = async () => {
    if (!deleteTarget || deleteTarget.kind !== "recurring") return;
    try {
      await api.delete(`/api/expenses/recurring/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotice("success", "Rule deleted", "The recurring rule was removed.");
      await fetchRecurringRules();
    } catch (err) {
      showNotice("error", "Delete failed", err?.response?.data?.error || "Unable to delete the recurring rule.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const exportExpensesToExcel = () => {
    const data = filteredExpenses.map((row) => ({
      Date: row.expense_date,
      Time: row.expense_datetime || "",
      Category: row.category_name || "",
      Vendor: row.vendor_name || "",
      Employee: row.employee_name || "",
      Amount: row.amount,
      Mode: row.payment_mode,
      Shift: row.shift || "",
      Reference: row.payment_reference || "",
      Description: row.description || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expenses");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buf], { type: "application/octet-stream" });
    saveAs(blob, "expenses.xlsx");
  };

  const expenseCards = [
    {
      tone: "blue",
      icon: FaMoneyBillWave,
      label: "Filtered total",
      value: money(expenseSummary.totalAmount),
      caption: `${expenseSummary.count} rows in view`,
    },
    {
      tone: "green",
      icon: FaWallet,
      label: "Cash spending",
      value: money(expenseSummary.cashAmount),
      caption: "Cash entries in the current range",
    },
    {
      tone: "amber",
      icon: FaChartLine,
      label: "Average ticket",
      value: money(expenseSummary.average),
      caption: expenseSummary.topCategory !== "-" ? `Top category: ${expenseSummary.topCategory}` : "No category data yet",
    },
    {
      tone: "slate",
      icon: FaClock,
      label: "Recurring rules",
      value: String(recurringSummary.count),
      caption: `${recurringSummary.activeAuto} auto-post enabled`,
    },
  ];

  return (
    <div className="cc-page text-light exp-page">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />
      <ConfirmModal
        open={!!deleteTarget}
        title={
          deleteTarget?.kind === "recurring"
            ? "Delete recurring rule?"
            : "Delete expense row?"
        }
        message={
          deleteTarget?.kind === "recurring"
            ? "This will remove the recurring expense rule from the schedule."
            : "This will remove the expense row from the register."
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={deleteTarget?.kind === "recurring" ? deleteRecurringRule : deleteExpense}
      />

      <section className="cc-hero">
        <div>
          <div className="cc-eyebrow">Expense Management</div>
          <h2>Expenses that feel calmer, clearer, and easier on mobile.</h2>
          <p>
            Capture spend quickly, filter it without friction, and keep recurring rules visible without
            letting the screen turn into a wall of forms.
          </p>
          <div className="cc-hero-meta">
            <span>{expenseSummary.count} visible rows</span>
            <span>{money(expenseSummary.totalAmount)} filtered total</span>
            <span>{recurringSummary.count} recurring rules</span>
          </div>
        </div>

        <div className="cc-hero-actions">
          <button type="button" className="btn cc-secondary-action" onClick={refreshAll} disabled={loading}>
            <FaRedoAlt aria-hidden="true" />
            Refresh
          </button>
          <button
            type="button"
            className="btn cc-primary-action"
            onClick={() => {
              resetExpenseForm();
              setTab(tab === "expenses" ? "recurring" : "expenses");
            }}
          >
            {tab === "expenses" ? "Go to recurring" : "Back to expenses"}
          </button>
        </div>
      </section>

      <section className="cc-context-strip">
        <div className="cc-context-head">
          <div>
            <span>Expense context</span>
            <h3>Pick the date range and filters before reading the cards</h3>
          </div>
          <div className="cc-context-chip">
            <strong>{filters.category_id ? categories.find((c) => String(c.id) === String(filters.category_id))?.name || "Filtered" : "All expenses"}</strong>
            <span>
              {filters.startDate ? toDateOnly(filters.startDate) : "Any start"} to{" "}
              {filters.endDate ? toDateOnly(filters.endDate) : "Any end"}
            </span>
          </div>
        </div>

        <div className="cc-context-grid exp-context-grid">
          <div>
            <label className="form-label">Start date</label>
            <DatePicker
              className="form-control"
              selected={filtersDraft.startDate}
              onChange={(d) => {
                const next = { ...filtersDraft, startDate: d || null };
                setFiltersDraft(next);
                setFilters(next);
                fetchExpenses(next);
              }}
              dateFormat="yyyy-MM-dd"
              popperClassName="cc-date-popper"
            />
          </div>
          <div>
            <label className="form-label">End date</label>
            <DatePicker
              className="form-control"
              selected={filtersDraft.endDate}
              onChange={(d) => {
                const next = { ...filtersDraft, endDate: d || null };
                setFiltersDraft(next);
                setFilters(next);
                fetchExpenses(next);
              }}
              dateFormat="yyyy-MM-dd"
              popperClassName="cc-date-popper"
            />
          </div>
          <div>
            <label className="form-label">Category</label>
            <select
              className="form-select"
              value={filtersDraft.category_id}
              onChange={(e) => {
                const next = { ...filtersDraft, category_id: e.target.value };
                setFiltersDraft(next);
                setFilters(next);
                fetchExpenses(next);
              }}
            >
              <option value="">All categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Payment mode</label>
            <select
              className="form-select"
              value={filtersDraft.payment_mode}
              onChange={(e) => {
                const next = { ...filtersDraft, payment_mode: e.target.value };
                setFiltersDraft(next);
                setFilters(next);
                fetchExpenses(next);
              }}
            >
              <option value="">All modes</option>
              {paymentModes.map((mode) => (
                <option key={mode} value={mode}>
                  {mode.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Shift</label>
            <select
              className="form-select"
              value={filtersDraft.shift}
              onChange={(e) => {
                const next = { ...filtersDraft, shift: e.target.value };
                setFiltersDraft(next);
                setFilters(next);
                fetchExpenses(next);
              }}
            >
              <option value="">All shifts</option>
              <option value="morning">Morning</option>
              <option value="night">Night</option>
            </select>
          </div>
        </div>

        <div className="cc-passbook-toolbar">
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(1)}>
            1D
          </button>
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(7)}>
            1W
          </button>
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(14)}>
            2W
          </button>
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(30)}>
            30D
          </button>
        </div>
      </section>

      <div className="cc-tabs" role="tablist" aria-label="Expense views">
        {[
          { id: "expenses", label: "Expenses" },
          { id: "recurring", label: "Recurring" },
        ].map((view) => (
          <button
            key={view.id}
            type="button"
            className={`cc-tab-btn ${tab === view.id ? "active" : ""}`}
            onClick={() => setTab(view.id)}
          >
            {view.label}
          </button>
        ))}
      </div>

      {tab === "expenses" ? (
        <>
          <section className="cc-summary-grid">
            {expenseCards.map((card) => (
              <StatCard key={card.label} {...card} />
            ))}
          </section>

          <section className="cc-grid exp-grid">
            <div className="cc-card cc-form-card">
              <div className="cc-card-head">
                <div>
                  <span>Expense entry</span>
                  <h3>{expenseForm.id ? "Edit a register row" : "Add a new expense"}</h3>
                </div>
                <div className="cc-ledger-chip">
                  <strong>Validation first</strong>
                  <span>Keep the entry clean</span>
                </div>
              </div>

              <div className="cc-form-grid">
                <div>
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={expenseForm.expense_date}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, expense_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Time (optional)</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={expenseForm.expense_datetime}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, expense_datetime: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={expenseForm.category_id}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, category_id: e.target.value }))}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Vendor</label>
                  <select
                    className="form-select"
                    value={expenseForm.vendor_id}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, vendor_id: e.target.value }))}
                  >
                    <option value="">Optional vendor</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    placeholder="0.00"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, amount: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Payment mode</label>
                  <select
                    className="form-select"
                    value={expenseForm.payment_mode}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, payment_mode: e.target.value }))}
                  >
                    {paymentModes.map((mode) => (
                      <option key={mode} value={mode}>
                        {mode.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Reference</label>
                  <input
                    className="form-control"
                    placeholder="UPI ref / cheque / bank ref"
                    value={expenseForm.payment_reference}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, payment_reference: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Shift</label>
                  <select
                    className="form-select"
                    value={expenseForm.shift}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, shift: e.target.value }))}
                  >
                    <option value="">Optional shift</option>
                    <option value="morning">Morning</option>
                    <option value="night">Night</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Employee</label>
                  <select
                    className="form-select"
                    value={expenseForm.employee_id}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, employee_id: e.target.value }))}
                  >
                    <option value="">Optional employee</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name || employee.username || `User ${employee.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="exp-full">
                  <label className="form-label">Description</label>
                  <textarea
                    rows="3"
                    className="form-control"
                    placeholder="Short note for the expense"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>
              </div>

              <div className="cc-action-row">
                <button type="button" className="btn cc-primary-action" onClick={saveExpense} disabled={savingExpense}>
                  <FaSave aria-hidden="true" />
                  {savingExpense ? "Saving..." : expenseForm.id ? "Update expense" : "Save expense"}
                </button>
                {expenseForm.id && (
                  <button type="button" className="btn btn-light" onClick={resetExpenseForm}>
                    Cancel edit
                  </button>
                )}
              </div>
            </div>

            <div className="cc-card cc-form-card">
              <div className="cc-card-head">
                <div>
                  <span>Expense insights</span>
                  <h3>See the spend shape before drilling into the table</h3>
                </div>
                <div className="cc-table-meta">
                  <span>Loaded</span>
                  <strong>{expenses.length}</strong>
                </div>
              </div>

              <div className="exp-insight-stack">
                <div className="exp-insight-block">
                  <div className="exp-insight-head">
                    <strong>Category mix</strong>
                    <span>{expenseSummary.topCategory || "-"}</span>
                  </div>
                  <div className="exp-bar-list">
                    {(expenseSummary.categoryList.slice(0, 4).length ? expenseSummary.categoryList.slice(0, 4) : [["No data", 0]]).map(([label, value]) => (
                      <MiniBar key={label} label={label} value={value} total={expenseSummary.totalAmount} tone="blue" />
                    ))}
                  </div>
                </div>

                <div className="exp-insight-block">
                  <div className="exp-insight-head">
                    <strong>Payment mix</strong>
                    <span>{expenseSummary.cashAmount > 0 ? "Cash is present" : "No cash rows yet"}</span>
                  </div>
                  <div className="exp-bar-list">
                    {(expenseSummary.modeList.slice(0, 4).length ? expenseSummary.modeList.slice(0, 4) : [["No data", 0]]).map(([label, value]) => (
                      <MiniBar key={label} label={String(label).toUpperCase()} value={value} total={expenseSummary.totalAmount} tone="green" />
                    ))}
                  </div>
                </div>

                <div className="exp-insight-foot">
                  <div>
                    <span>Latest month</span>
                    <strong>{expenseSummary.topMonth}</strong>
                  </div>
                  <div>
                    <span>Latest month total</span>
                    <strong>{money(expenseSummary.topMonthAmount)}</strong>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="cc-card cc-report-card">
            <div className="cc-card-head">
              <div>
                <span>Expense register</span>
                <h3>Mobile-friendly table with quick search and export</h3>
              </div>
              <div className="cc-table-meta">
                <span>{filteredExpenses.length} rows</span>
                <strong>
                  {safeExpensePage}/{expenseTotalPages}
                </strong>
              </div>
            </div>

            <div className="cc-filter-row">
              <div className="cc-search-shell">
                <FaSearch aria-hidden="true" />
                <input
                  className="form-control"
                  placeholder="Search category, vendor, employee, ref, note"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="cc-filter-group">
                <button type="button" className="btn btn-sm btn-light" onClick={exportExpensesToExcel}>
                  <FaFileExport aria-hidden="true" />
                  Export
                </button>
                <button type="button" className="btn btn-sm btn-light" onClick={() => setSearch("")}>
                  <FaFilter aria-hidden="true" />
                  Reset search
                </button>
              </div>
            </div>

            <div className="table-responsive cc-table-wrap">
              <table className="table cc-table">
                <thead>
                  <tr>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Date</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Category</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Vendor</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Amount</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Mode</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Shift</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Employee</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Description</th>
                    <th style={{ width: 160, color: "#fff", background: "#0f172a" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenseRows.map((row) => (
                    <tr key={row.id}>
                      <td data-label="Date">{row.expense_date}</td>
                      <td data-label="Category">
                        <strong>{row.category_name || "-"}</strong>
                        <span>{row.payment_reference || "No reference"}</span>
                      </td>
                      <td data-label="Vendor">{row.vendor_name || "-"}</td>
                      <td data-label="Amount">{money(row.amount)}</td>
                      <td data-label="Mode">
                        <span className={`cc-pill ${String(row.payment_mode || "").toLowerCase()}`}>{String(row.payment_mode || "-").toUpperCase()}</span>
                      </td>
                      <td data-label="Shift">{row.shift || "-"}</td>
                      <td data-label="Employee">
                        <strong>{row.employee_name || "-"}</strong>
                        <span>{row.employee_phone || "Expense row"}</span>
                      </td>
                      <td data-label="Description">{row.description || "-"}</td>
                      <td data-label="Actions" className="cc-row-actions">
                        <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => editExpense(row)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger cc-mini-btn"
                          onClick={() =>
                            setDeleteTarget({
                              kind: "expense",
                              id: row.id,
                            })
                          }
                        >
                          Del
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!expenseRows.length && (
                    <tr>
                      <td colSpan="9">
                        <div className="cc-empty-state">
                          <FaFilter aria-hidden="true" />
                          <strong>No expenses found</strong>
                          <span>Try a different date range, category, or search term.</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredExpenses.length > pageSize && (
              <div className="cc-pagination">
                <button
                  type="button"
                  className="cc-page-btn"
                  disabled={safeExpensePage === 1}
                  onClick={() => setExpensePage((page) => page - 1)}
                >
                  Prev
                </button>
                <div className="cc-page-list">
                  {getPageNumbers(safeExpensePage, expenseTotalPages).map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`cc-page-btn ${safeExpensePage === page ? "active" : ""}`}
                      onClick={() => setExpensePage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="cc-page-btn"
                  disabled={safeExpensePage === expenseTotalPages}
                  onClick={() => setExpensePage((page) => page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          <section className="cc-summary-grid">
            <StatCard
              tone="blue"
              icon={FaLayerGroup}
              label="Recurring rules"
              value={String(recurringSummary.count)}
              caption="Active schedules in rotation"
            />
            <StatCard
              tone="green"
              icon={FaClock}
              label="Auto-post"
              value={String(recurringSummary.activeAuto)}
              caption="Rules that post automatically"
            />
            <StatCard
              tone="amber"
              icon={FaMoneyBillWave}
              label="Scheduled value"
              value={money(recurringSummary.totalAmount)}
              caption="Combined recurring amount"
            />
            <StatCard
              tone="slate"
              icon={FaCheckCircle}
              label="Next run"
              value={recurringSummary.nextRun || "-"}
              caption="Closest scheduled rule"
            />
          </section>

          <section className="cc-grid exp-grid">
            <div className="cc-card cc-form-card">
              <div className="cc-card-head">
                <div>
                  <span>Recurring rule</span>
                  <h3>{recurForm.id ? "Edit a recurring rule" : "Add a new recurring rule"}</h3>
                </div>
                <div className="cc-ledger-chip">
                  <strong>Cadence</strong>
                  <span>Keep it predictable</span>
                </div>
              </div>

              <div className="cc-form-grid">
                <div>
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Shop rent, diesel card, power bill"
                    value={recurForm.name}
                    onChange={(e) => setRecurForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={recurForm.category_id}
                    onChange={(e) => setRecurForm((prev) => ({ ...prev, category_id: e.target.value }))}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Vendor</label>
                  <select
                    className="form-select"
                    value={recurForm.vendor_id}
                    onChange={(e) => setRecurForm((prev) => ({ ...prev, vendor_id: e.target.value }))}
                  >
                    <option value="">Optional vendor</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    placeholder="0.00"
                    value={recurForm.amount}
                    onChange={(e) => setRecurForm((prev) => ({ ...prev, amount: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Frequency</label>
                  <select
                    className="form-select"
                    value={recurForm.frequency}
                    onChange={(e) => setRecurForm((prev) => ({ ...prev, frequency: e.target.value }))}
                  >
                    {recurringFrequencies.map((frequency) => (
                      <option key={frequency} value={frequency}>
                        {frequency.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                {recurForm.frequency === "monthly" && (
                  <div>
                    <label className="form-label">Day of month</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      className="form-control"
                      value={recurForm.day_of_month}
                      onChange={(e) => setRecurForm((prev) => ({ ...prev, day_of_month: e.target.value }))}
                    />
                  </div>
                )}
                {recurForm.frequency === "weekly" && (
                  <div>
                    <label className="form-label">Day of week</label>
                    <input
                      type="number"
                      min="0"
                      max="6"
                      className="form-control"
                      value={recurForm.day_of_week}
                      onChange={(e) => setRecurForm((prev) => ({ ...prev, day_of_week: e.target.value }))}
                    />
                  </div>
                )}
                <div>
                  <label className="form-label">Start date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={recurForm.start_date}
                    onChange={(e) => setRecurForm((prev) => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">End date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={recurForm.end_date}
                    onChange={(e) => setRecurForm((prev) => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Next run date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={recurForm.next_run_date}
                    onChange={(e) => setRecurForm((prev) => ({ ...prev, next_run_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Payment mode</label>
                  <select
                    className="form-select"
                    value={recurForm.payment_mode}
                    onChange={(e) => setRecurForm((prev) => ({ ...prev, payment_mode: e.target.value }))}
                  >
                    {paymentModes.map((mode) => (
                      <option key={mode} value={mode}>
                        {mode.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="exp-switch-row">
                  <label className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={recurForm.auto_post}
                      onChange={(e) => setRecurForm((prev) => ({ ...prev, auto_post: e.target.checked }))}
                    />
                    <span>Auto post</span>
                  </label>
                  <label className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={recurForm.is_active}
                      onChange={(e) => setRecurForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                    />
                    <span>Active</span>
                  </label>
                </div>
                <div className="exp-full">
                  <label className="form-label">Notes</label>
                  <textarea
                    rows="3"
                    className="form-control"
                    placeholder="Optional explanation or billing detail"
                    value={recurForm.notes}
                    onChange={(e) => setRecurForm((prev) => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className="cc-action-row">
                <button
                  type="button"
                  className="btn cc-primary-action"
                  onClick={saveRecurringRule}
                  disabled={savingRecurring}
                >
                  <FaSave aria-hidden="true" />
                  {savingRecurring ? "Saving..." : recurForm.id ? "Update rule" : "Save rule"}
                </button>
                {recurForm.id && (
                  <button type="button" className="btn btn-light" onClick={resetRecurringForm}>
                    Cancel edit
                  </button>
                )}
              </div>
            </div>

            <div className="cc-card cc-form-card">
              <div className="cc-card-head">
                <div>
                  <span>Recurring insights</span>
                  <h3>Keep the cadence simple for the people using it</h3>
                </div>
                <div className="cc-table-meta">
                  <span>Searchable</span>
                  <strong>{filteredRecurringRules.length}</strong>
                </div>
              </div>

              <div className="exp-insight-stack">
                <div className="exp-insight-block">
                  <div className="exp-insight-head">
                    <strong>Upcoming load</strong>
                    <span>{recurringSummary.nextRun}</span>
                  </div>
                  <div className="exp-kpi-list">
                    <div>
                      <span>Total scheduled</span>
                      <strong>{money(recurringSummary.totalAmount)}</strong>
                    </div>
                    <div>
                      <span>Auto-post rules</span>
                      <strong>{recurringSummary.activeAuto}</strong>
                    </div>
                    <div>
                      <span>Recurring count</span>
                      <strong>{recurringSummary.count}</strong>
                    </div>
                  </div>
                </div>

                <div className="exp-insight-block">
                  <div className="exp-insight-head">
                    <strong>What to check</strong>
                    <span>Good recurring entries are specific and calm</span>
                  </div>
                  <div className="exp-note-list">
                    <div>Use one name per bill or vendor.</div>
                    <div>Set the next run date before saving.</div>
                    <div>Weekly and monthly rules need a valid day.</div>
                    <div>Keep payment mode and notes consistent.</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="cc-card cc-report-card">
            <div className="cc-card-head">
              <div>
                <span>Recurring register</span>
                <h3>Searchable recurring rules with compact mobile pagination</h3>
              </div>
              <div className="cc-table-meta">
                <span>{filteredRecurringRules.length} rows</span>
                <strong>
                  {safeRecurringPage}/{recurringTotalPages}
                </strong>
              </div>
            </div>

            <div className="cc-filter-row">
              <div className="cc-search-shell">
                <FaSearch aria-hidden="true" />
                <input
                  className="form-control"
                  placeholder="Search rule, category, vendor, note"
                  value={recurringSearch}
                  onChange={(e) => setRecurringSearch(e.target.value)}
                />
              </div>
              <div className="cc-filter-group">
                <button type="button" className="btn btn-sm btn-light" onClick={() => setTab("expenses")}>
                  <FaMoneyBillWave aria-hidden="true" />
                  Back to expenses
                </button>
                <button type="button" className="btn btn-sm btn-light" onClick={resetRecurringForm}>
                  <FaFilter aria-hidden="true" />
                  Reset form
                </button>
              </div>
            </div>

            <div className="table-responsive cc-table-wrap">
              <table className="table cc-table">
                <thead>
                  <tr>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Name</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Category</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Vendor</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Amount</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Freq</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Next run</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Auto</th>
                    <th style={{ color: "#fff", background: "#0f172a" }}>Active</th>
                    <th style={{ width: 160, color: "#fff", background: "#0f172a" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recurringRows.map((row) => (
                    <tr key={row.id}>
                      <td data-label="Name">
                        <strong>{row.name}</strong>
                        <span>{row.notes || "Recurring expense"}</span>
                      </td>
                      <td data-label="Category">{row.category_name || "-"}</td>
                      <td data-label="Vendor">{row.vendor_name || "-"}</td>
                      <td data-label="Amount">{money(row.amount)}</td>
                      <td data-label="Freq">{String(row.frequency || "-").toUpperCase()}</td>
                      <td data-label="Next run">{row.next_run_date || "-"}</td>
                      <td data-label="Auto">{Number(row.auto_post || 0) ? "Yes" : "No"}</td>
                      <td data-label="Active">{Number(row.is_active || 0) ? "Yes" : "No"}</td>
                      <td data-label="Actions" className="cc-row-actions">
                        <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => editRecurringRule(row)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger cc-mini-btn"
                          onClick={() =>
                            setDeleteTarget({
                              kind: "recurring",
                              id: row.id,
                            })
                          }
                        >
                          Del
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!recurringRows.length && (
                    <tr>
                      <td colSpan="9">
                        <div className="cc-empty-state">
                          <FaLayerGroup aria-hidden="true" />
                          <strong>No recurring rules found</strong>
                          <span>Try another keyword or add a new rule.</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredRecurringRules.length > pageSize && (
              <div className="cc-pagination">
                <button
                  type="button"
                  className="cc-page-btn"
                  disabled={safeRecurringPage === 1}
                  onClick={() => setRecurringPage((page) => page - 1)}
                >
                  Prev
                </button>
                <div className="cc-page-list">
                  {getPageNumbers(safeRecurringPage, recurringTotalPages).map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`cc-page-btn ${safeRecurringPage === page ? "active" : ""}`}
                      onClick={() => setRecurringPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="cc-page-btn"
                  disabled={safeRecurringPage === recurringTotalPages}
                  onClick={() => setRecurringPage((page) => page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
